/**
 * Complete Step Dialog
 * 
 * Modal dialog for completing a workflow step with dynamic custom fields.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { Loader2, CheckCircle2, ArrowRight, X, AlertCircle } from 'lucide-react';

// Utility for class names
const cn = (...classes) => classes.filter(Boolean).join(' ');

// Field renderer based on field type
function FieldRenderer({ field, value, onChange, error }) {
  const commonProps = {
    id: field.field_key,
    'data-testid': `field-${field.field_key}`,
  };

  switch (field.field_type) {
    case 'text':
      return (
        <Input
          {...commonProps}
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''}
          className={cn(error && 'border-red-500')}
        />
      );

    case 'textarea':
      return (
        <Textarea
          {...commonProps}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''}
          rows={3}
          className={cn(error && 'border-red-500')}
        />
      );

    case 'number':
      return (
        <Input
          {...commonProps}
          type="number"
          value={value || ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
          placeholder={field.placeholder || ''}
          min={field.validation_rules?.min}
          max={field.validation_rules?.max}
          className={cn(error && 'border-red-500')}
        />
      );

    case 'date':
      return (
        <Input
          {...commonProps}
          type="date"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={cn(error && 'border-red-500')}
        />
      );

    case 'datetime':
      return (
        <Input
          {...commonProps}
          type="datetime-local"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={cn(error && 'border-red-500')}
        />
      );

    case 'select':
      return (
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger className={cn(error && 'border-red-500')} data-testid={`field-${field.field_key}`}>
            <SelectValue placeholder={field.placeholder || 'Select an option'} />
          </SelectTrigger>
          <SelectContent>
            {(field.field_options || []).map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case 'multiselect':
      // For multiselect, use checkboxes
      return (
        <div className="space-y-2">
          {(field.field_options || []).map((option) => (
            <label key={option.value} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={(value || []).includes(option.value)}
                onCheckedChange={(checked) => {
                  const current = value || [];
                  if (checked) {
                    onChange([...current, option.value]);
                  } else {
                    onChange(current.filter(v => v !== option.value));
                  }
                }}
              />
              <span className="text-sm">{option.label}</span>
            </label>
          ))}
        </div>
      );

    case 'checkbox':
      return (
        <div className="flex items-center gap-2">
          <Checkbox
            {...commonProps}
            checked={value || false}
            onCheckedChange={onChange}
          />
          <span className="text-sm text-gray-600">{field.help_text || 'Check if applicable'}</span>
        </div>
      );

    case 'file':
      return (
        <Input
          {...commonProps}
          type="file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              // For now, just store the filename
              onChange(file.name);
            }
          }}
          className={cn(error && 'border-red-500')}
        />
      );

    default:
      return (
        <Input
          {...commonProps}
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''}
        />
      );
  }
}

export default function CompleteStepDialog({ 
  open, 
  onOpenChange, 
  task, 
  onComplete,
  isLoading = false 
}) {
  const [fieldValues, setFieldValues] = useState({});
  const [notes, setNotes] = useState('');
  const [selectedAction, setSelectedAction] = useState(null);
  const [errors, setErrors] = useState({});

  // Get step details
  const step = task?.step;
  const fields = step?.fields || [];
  const transitions = task?.available_transitions || [];

  // Initialize field values from existing values
  useEffect(() => {
    if (task?.field_values) {
      setFieldValues(task.field_values);
    } else {
      setFieldValues({});
    }
    setNotes('');
    setSelectedAction(null);
    setErrors({});
  }, [task]);

  // Get default action if there's only one transition
  useEffect(() => {
    if (transitions.length === 1) {
      setSelectedAction(transitions[0].action_name);
    }
  }, [transitions]);

  const validateFields = () => {
    const newErrors = {};
    
    fields.forEach((field) => {
      if (field.is_required) {
        const value = fieldValues[field.field_key];
        if (value === undefined || value === null || value === '' || 
            (Array.isArray(value) && value.length === 0)) {
          newErrors[field.field_key] = 'This field is required';
        }
      }
      
      // Number validation
      if (field.field_type === 'number' && fieldValues[field.field_key]) {
        const value = fieldValues[field.field_key];
        if (field.validation_rules?.min !== undefined && value < field.validation_rules.min) {
          newErrors[field.field_key] = `Minimum value is ${field.validation_rules.min}`;
        }
        if (field.validation_rules?.max !== undefined && value > field.validation_rules.max) {
          newErrors[field.field_key] = `Maximum value is ${field.validation_rules.max}`;
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateFields()) {
      return;
    }

    onComplete({
      action_name: selectedAction || 'complete',
      field_values: fieldValues,
      notes: notes || null,
      signoff_notes: step?.requires_signoff ? notes : null,
    });
  };

  const handleFieldChange = (fieldKey, value) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldKey]: value
    }));
    // Clear error when field is changed
    if (errors[fieldKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  // Get action button colors
  const getActionButtonClass = (action) => {
    switch (action.action_color) {
      case 'green':
        return 'bg-green-600 hover:bg-green-700 text-white';
      case 'red':
        return 'bg-red-600 hover:bg-red-700 text-white';
      case 'yellow':
        return 'bg-yellow-500 hover:bg-yellow-600 text-white';
      case 'blue':
        return 'bg-blue-600 hover:bg-blue-700 text-white';
      default:
        return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="complete-step-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            Complete Task
          </DialogTitle>
          <DialogDescription>
            {step?.name || 'Complete this workflow step'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Step Description */}
          {step?.description && (
            <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
              {step.description}
            </div>
          )}

          {/* Signoff Required Badge */}
          {step?.requires_signoff && (
            <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-yellow-600" />
              <span className="text-sm text-yellow-700">This step requires your sign-off</span>
            </div>
          )}

          {/* Dynamic Fields */}
          {fields.length > 0 && (
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Required Information</h4>
              {fields.map((field) => (
                <div key={field.id} className="space-y-1.5">
                  <Label htmlFor={field.field_key} className="flex items-center gap-1">
                    {field.field_label}
                    {field.is_required && <span className="text-red-500">*</span>}
                  </Label>
                  {field.help_text && field.field_type !== 'checkbox' && (
                    <p className="text-xs text-gray-500">{field.help_text}</p>
                  )}
                  <FieldRenderer
                    field={field}
                    value={fieldValues[field.field_key]}
                    onChange={(value) => handleFieldChange(field.field_key, value)}
                    error={errors[field.field_key]}
                  />
                  {errors[field.field_key] && (
                    <p className="text-xs text-red-500">{errors[field.field_key]}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes or comments..."
              rows={2}
              data-testid="completion-notes"
            />
          </div>

          {/* Action Selection (if multiple transitions) */}
          {transitions.length > 1 && (
            <div className="space-y-2">
              <Label>Select Action</Label>
              <div className="flex flex-wrap gap-2">
                {transitions.map((transition) => (
                  <Button
                    key={transition.id}
                    type="button"
                    variant={selectedAction === transition.action_name ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedAction(transition.action_name)}
                    className={cn(
                      selectedAction === transition.action_name && getActionButtonClass(transition)
                    )}
                    data-testid={`action-${transition.action_name}`}
                  >
                    {transition.action_label || transition.action_name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || (transitions.length > 1 && !selectedAction)}
            data-testid="submit-completion"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Completing...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Complete Task
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
